<template>
  <slide-out
    dock="right"
    :size="panelSize"
    :min-size="panelMinSize"
    :allow-resize="true"
    append-to="#app"
    :visible.sync="slideout.active"
    v-on:close="close">
    <template v-slot:header>
      <v-row class="ma-0 d-flex flex-column">
        <v-row class="ma-0 d-flex justify-end">
          <v-btn @click="close" icon>
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-row>
        <v-row class="ma-0 py-2 d-flex justify-space-between align-center">
          <h3 class="font-weight-bold pa-1">
            <v-icon left>mdi-filter</v-icon> Global Filters
          </h3>
          <div class="d-flex align-center">
            <v-chip
              v-if="activeFilterCount > 0"
              small
              color="primary"
              class="mr-2">
              {{ activeFilterCount }} active
            </v-chip>
            <v-btn text color="error" @click="resetAllFilters" :disabled="activeFilterCount === 0">
              Reset all
            </v-btn>
          </div>
        </v-row>
      </v-row>
    </template>

    <div class="d-flex py-3 px-6 flex-column global-filters-body">
      <!-- Loading overlay -->
      <div v-if="loading" class="global-filters-loading">
        <envizion-loader :size="48" :color="$vuetify.theme.dark ? '#2cc295' : '#03624c'" label="Loading filter options" />
      </div>

      <div v-else class="global-filters-sections">

        <!-- Facility filter retired: the multi-select bound to no backend property yet
             counted as an active filter (looks-filtered-isn't). Real cross-facility
             filtering is a backlog design — it needs a DTO field, filter semantics, and
             facility authorization. Markup kept below as the starting point. -->
        <!-- <div class="global-filter-section">
          <div class="global-filter-section__label">
            <v-icon small class="global-filter-section__icon mr-2">mdi-domain</v-icon>
            Facility
          </div>
          <v-autocomplete
            v-model="selectedFilters.facility"
            :items="facilityOptions"
            item-text="name"
            item-value="id"
            outlined
            dense
            hide-details
            multiple
            clearable
            attach
            color="primary"
            placeholder="All Facilities"
            :menu-props="{ offsetY: true, maxHeight: 260 }">
            <template v-slot:selection="{ item, index }">
              <v-chip small color="primary" close @click:close="removeFacility(index)" v-if="index < 2">
                <span class="text-truncate" style="max-width: 120px">{{ item.name }}</span>
              </v-chip>
              <span v-if="index === 2" class="grey--text text-caption ml-1">
                +{{ selectedFilters.facility.length - 2 }}
              </span>
            </template>
          </v-autocomplete>
        </div> -->

        <!-- Date Range -->
        <div class="global-filter-section">
          <div class="global-filter-section__label">
            <v-icon small class="global-filter-section__icon mr-2">mdi-calendar-range</v-icon>
            Date Range
          </div>
          <v-select
            v-model="dateRangePreset"
            :items="dateRangeOptions"
            item-text="text"
            item-value="value"
            outlined
            dense
            hide-details
            clearable
            attach
            color="primary"
            placeholder="All Time"
            :menu-props="{ offsetY: true }"
            @change="onDatePresetChange">
            <template v-slot:item="{ item }">
              <v-icon small class="mr-2" style="opacity: .5">{{ item.icon }}</v-icon>
              {{ item.text }}
            </template>
          </v-select>
          <!-- Custom range pickers (only visible when "Custom Range" is selected) -->
          <div v-if="dateRangePreset === 'custom'" class="global-filter-date-range mt-2">
            <v-menu
              v-model="dateFromMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  :value="selectedFilters.dateFrom"
                  outlined
                  dense
                  hide-details
                  readonly
                  clearable
                  color="primary"
                  prepend-inner-icon="mdi-calendar-start"
                  placeholder="From"
                  v-bind="attrs"
                  v-on="on"
                  @click:clear="selectedFilters.dateFrom = null" />
              </template>
              <v-date-picker
                :value="selectedFilters.dateFrom"
                no-title
                scrollable
                color="primary"
                @input="onDateFromPick" />
            </v-menu>

            <v-icon small color="grey" class="mx-1">mdi-arrow-right</v-icon>

            <v-menu
              v-model="dateToMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  :value="selectedFilters.dateTo"
                  outlined
                  dense
                  hide-details
                  readonly
                  clearable
                  color="primary"
                  prepend-inner-icon="mdi-calendar-end"
                  placeholder="To"
                  v-bind="attrs"
                  v-on="on"
                  @click:clear="selectedFilters.dateTo = null" />
              </template>
              <v-date-picker
                :value="selectedFilters.dateTo"
                no-title
                scrollable
                color="primary"
                :min="selectedFilters.dateFrom || undefined"
                @input="onDateToPick" />
            </v-menu>
          </div>
        </div>

      </div>
    </div>
  </slide-out>
</template>

<script>
/* eslint-disable */
import { log, error } from '@/utils/debugLogger';

export default {
  name: 'GlobalFiltersPanel',
  props: {
    viewerOpened: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      slideout: { active: false },
      loading: false,
      panelSize: 380,
      panelMinSize: 320,
      dateFromMenu: false,
      dateToMenu: false,
      dateRangePreset: null,
      dateRangeOptions: [
        { text: 'Today', value: 'today', icon: 'mdi-calendar-today' },
        { text: 'Yesterday', value: 'yesterday', icon: 'mdi-calendar-arrow-left' },
        { text: 'This Week', value: 'this_week', icon: 'mdi-calendar-week' },
        { text: 'Last Week', value: 'last_week', icon: 'mdi-calendar-week' },
        { text: 'This Month', value: 'this_month', icon: 'mdi-calendar-month' },
        { text: 'Last Month', value: 'last_month', icon: 'mdi-calendar-month' },
        { text: 'This Quarter', value: 'this_quarter', icon: 'mdi-calendar-text' },
        { text: 'This Year', value: 'this_year', icon: 'mdi-calendar-star' },
        { text: 'Last Year', value: 'last_year', icon: 'mdi-calendar-star' },
        { text: 'Custom Range', value: 'custom', icon: 'mdi-calendar-range' },
      ],
      facilityOptions: [],
    };
  },
  computed: {
    assetId() {
      return this.$route.params.id;
    },
    selectedFilters: {
      get() {
        return this.$store.getters['GLOBAL_DASHBOARD_FILTERS/GET_FILTERS'];
      },
      set(val) {
        this.$store.commit('GLOBAL_DASHBOARD_FILTERS/SET_FILTERS', val);
      },
    },
    activeFilterCount() {
      const f = this.selectedFilters;
      let count = 0;
      // facility excluded: the control is retired and its value binds to no backend
      // filter, so counting it would report a filter that is never applied.
      if (f.dateFrom || f.dateTo) count++;
      return count;
    },
  },
  methods: {
    open() {
      this.slideout.active = true;
      this.fetchAllOptions();
    },
    close() {
      this.slideout.active = false;
    },

    async fetchAllOptions() {
      this.loading = true;
      try {
        await this.$store.dispatch('FACILITY/FETCH_ACCESSIBLE', { force: false });
        this.facilityOptions = this.$store.state.FACILITY?.accessibleEntries || [];
      } catch (err) {
        error('GlobalFiltersPanel', 'Failed to fetch filter options', err);
      } finally {
        this.loading = false;
      }
    },

    resetAllFilters() {
      this.$store.commit('GLOBAL_DASHBOARD_FILTERS/RESET_FILTERS');
      this.dateRangePreset = null;
    },
    removeFacility(index) {
      this.selectedFilters.facility.splice(index, 1);
    },
    onDatePresetChange(preset) {
      if (!preset) {
        this.selectedFilters.dateFrom = null;
        this.selectedFilters.dateTo = null;
        return;
      }
      if (preset === 'custom') return;

      const today = new Date();
      const yyyy = (d) => d.toISOString().split('T')[0];
      let from, to;

      switch (preset) {
        case 'today':
          from = to = yyyy(today);
          break;
        case 'yesterday': {
          const d = new Date(today);
          d.setDate(d.getDate() - 1);
          from = to = yyyy(d);
          break;
        }
        case 'this_week': {
          const day = today.getDay();
          const startOfWeek = new Date(today);
          startOfWeek.setDate(today.getDate() - (day === 0 ? 6 : day - 1));
          from = yyyy(startOfWeek);
          to = yyyy(today);
          break;
        }
        case 'last_week': {
          const day = today.getDay();
          const endOfLastWeek = new Date(today);
          endOfLastWeek.setDate(today.getDate() - (day === 0 ? 7 : day));
          const startOfLastWeek = new Date(endOfLastWeek);
          startOfLastWeek.setDate(endOfLastWeek.getDate() - 6);
          from = yyyy(startOfLastWeek);
          to = yyyy(endOfLastWeek);
          break;
        }
        case 'this_month':
          from = yyyy(new Date(today.getFullYear(), today.getMonth(), 1));
          to = yyyy(today);
          break;
        case 'last_month': {
          const firstOfLast = new Date(today.getFullYear(), today.getMonth() - 1, 1);
          const lastOfLast = new Date(today.getFullYear(), today.getMonth(), 0);
          from = yyyy(firstOfLast);
          to = yyyy(lastOfLast);
          break;
        }
        case 'this_quarter': {
          const qMonth = Math.floor(today.getMonth() / 3) * 3;
          from = yyyy(new Date(today.getFullYear(), qMonth, 1));
          to = yyyy(today);
          break;
        }
        case 'this_year':
          from = yyyy(new Date(today.getFullYear(), 0, 1));
          to = yyyy(today);
          break;
        case 'last_year':
          from = yyyy(new Date(today.getFullYear() - 1, 0, 1));
          to = yyyy(new Date(today.getFullYear() - 1, 11, 31));
          break;
      }

      this.selectedFilters.dateFrom = from;
      this.selectedFilters.dateTo = to;
    },
    onDateFromPick(val) {
      this.selectedFilters.dateFrom = val;
      this.dateFromMenu = false;
    },
    onDateToPick(val) {
      this.selectedFilters.dateTo = val;
      this.dateToMenu = false;
    },
  },
  watch: {
    selectedFilters: {
      handler(val) {
        if (this.slideout.active) {
          this.$emit('filtersChanges', val);
        }
      },
      deep: true,
    },
  },
};
</script>

<style lang="scss" scoped>
/* Body */
.global-filters-body {
  overflow-y: auto;
  height: calc(100vh - 120px);
}

.global-filters-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 0;
}

/* Filter sections */
.global-filters-sections {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.global-filter-section {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.global-filter-section__label {
  display: flex;
  align-items: center;
  font-family: 'Poppins', sans-serif;
  font-size: var(--ds-font-body-sm, 13px);
  font-weight: 600;
  color: rgba(255, 255, 255, 0.8);
  letter-spacing: 0.02em;
}

/* Mono-color icons for filter labels */
.global-filter-section__icon {
  opacity: 0.6;
}

.theme--light .global-filter-section__label {
  color: rgba(0, 0, 0, 0.7);
}

/* Date range row */
.global-filter-date-range {
  display: flex;
  align-items: center;
  gap: 4px;
}

.global-filter-date-range .v-text-field {
  flex: 1;
  min-width: 0;
}

</style>
